import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  useExportSubscriptionReportMutation,
  useExportSubscriptionRedemptionsMutation,
  useGetSubscriptionOutletBreakdownMutation,
  useGetSubscriptionPlanBreakdownMutation,
  useGetSubscriptionRedemptionListQuery,
  useGetSubscriptionReportSummaryMutation,
} from "../../../store/queries/Subscription";

/**
 * The four sections are four separate endpoints, so narrowing the view also narrows the
 * fetching - "all" is the original behaviour and costs four requests, any single section
 * costs one. The ids double as the keys the visibility flags are derived from.
 */
export const REPORT_TYPES = [
  { id: "all", name: "All reports" },
  { id: "summary", name: "Summary" },
  { id: "plans", name: "By plan" },
  { id: "outlets", name: "By outlet" },
  { id: "redemptions", name: "Redemption ledger" },
];

/**
 * Which ledger movements to list. Redemptions lead because the section sits under a report
 * whose every other figure is redemption-based, and because a write-off shown among them
 * reads as a beverage collected when it is the opposite. "All movements" is the way to the
 * whole ledger; the service takes that id verbatim.
 */
export const LEDGER_TYPES = [
  { id: "debit", name: "Redemptions" },
  { id: "all", name: "All movements" },
  { id: "credit_void", name: "Credited back (void)" },
  { id: "credit_manual", name: "Credited back (manual)" },
  { id: "release_unpaid", name: "Released (unpaid)" },
  { id: "cancel_forfeit", name: "Written off (cancelled)" },
];

/** The service defaults to the last 30 days; the pickers open on the same window. */
const isoDay = (date) => date.toISOString().slice(0, 10);
const defaultStart = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return isoDay(date);
};

const useSubscriptionReport = () => {
  const [filters, setFilters] = useState({
    start_date: defaultStart(),
    end_date: isoDay(new Date()),
  });
  const [page, setPage] = useState(1);
  const [email, setEmail] = useState("");
  // Opens on "By plan" - the plan breakdown is the figure this report gets pulled up for most,
  // and "All reports" costs three extra requests to render tables nobody scrolled to yet.
  const [reportType, setReportType] = useState("plans");
  const [ledgerType, setLedgerType] = useState(LEDGER_TYPES[0].id);

  const showSummary = reportType === "all" || reportType === "summary";
  const showPlans = reportType === "all" || reportType === "plans";
  const showOutlets = reportType === "all" || reportType === "outlets";
  const showLedger = reportType === "all" || reportType === "redemptions";

  const [summary, setSummary] = useState({});
  const [plans, setPlans] = useState([]);
  const [outlets, setOutlets] = useState([]);

  const [fetchSummary, { isLoading: isSummaryLoading }] =
    useGetSubscriptionReportSummaryMutation();
  const [fetchPlans] = useGetSubscriptionPlanBreakdownMutation();
  const [fetchOutlets] = useGetSubscriptionOutletBreakdownMutation();
  const [exportReport] = useExportSubscriptionReportMutation();
  const [exportRedemptions] = useExportSubscriptionRedemptionsMutation();

  const { data: redemptionList = {}, isFetching: isLedgerFetching } =
    useGetSubscriptionRedemptionListQuery(
      {
        ...filters,
        type: ledgerType,
        page,
        // page_size, not per_page: the base query renames this one on the way out and drops
        // a literal per_page, so the latter never reached the service at all. It read as
        // correct only because the default it fell back to was the ten a page this asks
        // for - the day someone changed the number, nothing would have moved.
        page_size: 10,
      },
      { skip: !showLedger }
    );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [summaryRes, planRes, outletRes] = await Promise.all([
        showSummary ? fetchSummary(filters) : null,
        showPlans ? fetchPlans(filters) : null,
        showOutlets ? fetchOutlets(filters) : null,
      ]);

      if (cancelled) {
        return;
      }

      // Only the sections on screen are written back, so switching away and back does not
      // blank a table with the empty default before its refetch lands.
      if (showSummary) {
        setSummary(summaryRes?.data?.data ?? {});
      }
      if (showPlans) {
        setPlans(planRes?.data?.data ?? []);
      }
      if (showOutlets) {
        setOutlets(outletRes?.data?.data ?? []);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    //eslint-disable-next-line
  }, [filters, reportType]);

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
    setPage(1);
  };

  const handleReportTypeChange = (value) => {
    setReportType(value ?? REPORT_TYPES[0].id);
    setPage(1);
  };

  const handleLedgerTypeChange = (value) => {
    setLedgerType(value ?? LEDGER_TYPES[0].id);
    setPage(1);
  };

  /**
   * Both exports are queued and mailed, so success here means "accepted", not "ready" - the
   * wording has to say so or the team will sit waiting for a download that never starts.
   */
  const handleExport = async (which) => {
    if (!email) {
      toast.error(`Enter an email address to send the report to`);
      return;
    }

    const action = which === "redemptions" ? exportRedemptions : exportReport;

    // The ledger export carries the same type filter as the table, or the file that lands is
    // not the thing the reader was looking at when they asked for it.
    const response = await action({
      ...filters,
      email,
      ...(which === "redemptions" ? { type: ledgerType } : {}),
    });

    if (response?.data?.status_code === 200) {
      toast.success(`The report will be emailed to ${email} shortly`);
    } else {
      toast.error(
        response?.error?.data?.message ??
          response?.data?.message ??
          `Unable to queue the report`
      );
    }
  };

  return {
    page,
    email,
    plans,
    outlets,
    filters,
    summary,
    setPage,
    setEmail,
    showPlans,
    showLedger,
    ledgerType,
    reportType,
    showSummary,
    showOutlets,
    redemptionList,
    isLedgerFetching,
    isSummaryLoading,
    handleFilterChange,
    handleReportTypeChange,
    handleLedgerTypeChange,
    handleExport,
  };
};

export default useSubscriptionReport;
